
/**
 * Niiko Storage Service - IndexedDB Implementation
 * Substitui o localStorage para suportar armazenamento em larga escala (Imagens/Vídeos Base64).
 */

const DB_NAME = 'NiikoArtworkDB';
const DB_VERSION = 8; // Versão incrementada para adicionar commission_tiers e settings
const STORES = ['arts', 'orders', 'chats', 'comments', 'session', 'users', 'category_covers', 'deleted_orders', 'deleted_chats', 'deleted_arts', 'commission_tiers', 'settings'];

export class StorageService {
  private db: IDBDatabase | null = null;
  private isFallback = false;
  private memoryStorage: Record<string, Record<string, any>> = {};

  constructor() {
    STORES.forEach(store => {
      this.memoryStorage[store] = {};
    });
  }

  private async getDB(): Promise<IDBDatabase | null> {
    if (this.isFallback) return null;
    if (this.db) return this.db;

    return new Promise((resolve) => {
      try {
        if (typeof indexedDB === 'undefined') {
          console.warn('IndexedDB não suportado. Usando fallback em memória.');
          this.isFallback = true;
          resolve(null);
          return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
          try {
            const db = (event.target as IDBOpenDBRequest).result;
            STORES.forEach(store => {
              if (!db.objectStoreNames.contains(store)) {
                db.createObjectStore(store);
                console.log(`Store criada: ${store}`);
              }
            });
          } catch (e) {
            console.error('Erro ao atualizar IndexedDB:', e);
          }
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          resolve(this.db);
        };

        request.onerror = (event) => {
          console.warn('Erro ao abrir IndexedDB, ativando fallback em memória:', event);
          this.isFallback = true;
          resolve(null);
        };
      } catch (e) {
        console.warn('Erro síncrono ao abrir IndexedDB, ativando fallback em memória:', e);
        this.isFallback = true;
        resolve(null);
      }
    });
  }

  async save(storeName: string, data: any, key: string = 'data'): Promise<void> {
    const db = await this.getDB();
    if (this.isFallback || !db) {
      if (!this.memoryStorage[storeName]) {
        this.memoryStorage[storeName] = {};
      }
      try {
        this.memoryStorage[storeName][key] = JSON.parse(JSON.stringify(data));
      } catch (e) {
        this.memoryStorage[storeName][key] = data;
      }
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(data, key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(`Erro ao salvar em ${storeName}`);
      } catch (e) {
        reject(`Store ${storeName} não encontrada no banco de dados.`);
      }
    });
  }

  async load(storeName: string, key: string = 'data'): Promise<any> {
    const db = await this.getDB();
    if (this.isFallback || !db) {
      if (!this.memoryStorage[storeName]) {
        this.memoryStorage[storeName] = {};
      }
      const data = this.memoryStorage[storeName][key];
      try {
        return data !== undefined ? JSON.parse(JSON.stringify(data)) : null;
      } catch (e) {
        return data || null;
      }
    }

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
          console.warn(`Erro ao ler de ${storeName}`);
          resolve(null);
        };
      } catch (e) {
        console.warn(`Tentativa de carregar store inexistente ou erro: ${storeName}`);
        resolve(null);
      }
    });
  }

  async clearAll(): Promise<void> {
    STORES.forEach(s => {
      this.memoryStorage[s] = {};
    });

    const db = await this.getDB();
    if (this.isFallback || !db) {
      return;
    }

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction(STORES, 'readwrite');
        STORES.forEach(s => {
          if (db.objectStoreNames.contains(s)) {
            transaction.objectStore(s).clear();
          }
        });
        transaction.oncomplete = () => resolve();
      } catch (e) {
        resolve();
      }
    });
  }
}

export const storage = new StorageService();
